import type { MaybeRefOrGetter } from 'vue';

import { storeToRefs } from 'pinia';
import { computed, toValue } from 'vue';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';

type ProductLinkTarget = {
  id: string;
  bookingUrl?: string | null;
};

/**
 * Resolves the URL of a product detail page on the host site.
 *
 * Priority:
 * 1. `bookingUrl` when the host disabled the detail view (`options.hasDetailView === false`)
 * 2. Canonical URL provided by the host (Woody `canonicalsEndpoint`)
 * 3. `prefixUrl + id` (Woody `youbook_single` route, which redirects to the canonical page)
 *
 * `prefixUrl` defaults to the global configuration when not provided.
 * Returns `null` when no link can be built (detail view disabled and no `bookingUrl`).
 */
export function useProductLink(
  product: MaybeRefOrGetter<ProductLinkTarget>,
  prefixUrl?: MaybeRefOrGetter<string | null>,
) {
  const globalConfigurationStore = useGlobalConfigurationStore();
  const { globalConfiguration, canonicals } = storeToRefs(globalConfigurationStore);

  const resolvedPrefixUrl = computed(() =>
    prefixUrl === undefined
      ? globalConfiguration.value?.prefixUrl ?? null
      : toValue(prefixUrl),
  );

  const productLink = computed<string | null>(() => {
    const { id, bookingUrl } = toValue(product);

    if (globalConfiguration.value?.options?.hasDetailView === false)
      return bookingUrl || null;

    const canonical = canonicals.value[id];
    if (canonical)
      return canonical;

    return resolvedPrefixUrl.value
      ? `${resolvedPrefixUrl.value}${id}`
      : `${window.location.origin}/youbook_single/${id}`;
  });

  return { productLink };
}
